// Meta Pixel + Google (GA4 + Google Ads) conversion bridge.
// Pixel IDs are PUBLIC by design (they ship in client JS), so they live
// either in window.AETHERION_PIXELS (set via index.html for quick edits)
// or in VITE_* env vars. If no IDs are configured, every call is a no-op
// and no script tags are injected.

import type { FunnelEvent } from "./funnel";

type PixelConfig = {
  metaPixelId?: string;            // e.g. "123456789012345"
  ga4Id?: string;                  // e.g. "G-XXXXXXXXXX"
  gadsConversionId?: string;       // e.g. "AW-123456789"
  gadsLabels?: Partial<Record<FunnelEvent, string>>; // e.g. { auth_completed: "AbC-D_efGhIj" }
};

declare global {
  interface Window {
    AETHERION_PIXELS?: PixelConfig;
    fbq?: ((...args: unknown[]) => void) & { callMethod?: unknown; queue?: unknown[]; loaded?: boolean; version?: string; push?: unknown };
    _fbq?: unknown;
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

let initialized = false;

function readConfig(): PixelConfig {
  const win = (typeof window !== "undefined" ? window.AETHERION_PIXELS : undefined) ?? {};
  const env = (import.meta as { env?: Record<string, string | undefined> }).env ?? {};
  return {
    metaPixelId:        win.metaPixelId        || env.VITE_META_PIXEL_ID,
    ga4Id:              win.ga4Id              || env.VITE_GA4_ID,
    gadsConversionId:   win.gadsConversionId   || env.VITE_GADS_CONVERSION_ID,
    gadsLabels: {
      lp_cast_completed:     win.gadsLabels?.lp_cast_completed     || env.VITE_GADS_LABEL_LP_CAST,
      waitlist_submitted:    win.gadsLabels?.waitlist_submitted    || env.VITE_GADS_LABEL_WAITLIST,
      auth_completed:        win.gadsLabels?.auth_completed        || env.VITE_GADS_LABEL_SIGNUP,
      first_paid_conversion: win.gadsLabels?.first_paid_conversion || env.VITE_GADS_LABEL_PURCHASE,
      ...(win.gadsLabels ?? {}),
    },
  };
}

function injectScript(src: string, async = true): void {
  if (typeof document === "undefined") return;
  if (document.querySelector(`script[src="${src}"]`)) return;
  const s = document.createElement("script");
  s.src = src;
  s.async = async;
  document.head.appendChild(s);
}

function initMeta(pixelId: string): void {
  if (typeof window === "undefined" || window.fbq) return;
  /* eslint-disable */
  (function (f: any, b: any, e: any, v: any) {
    if (f.fbq) return;
    const n: any = (f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    });
    if (!f._fbq) f._fbq = n;
    n.push = n; n.loaded = true; n.version = "2.0"; n.queue = [];
    const t = b.createElement(e); t.async = true; t.src = v;
    const s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s);
  })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
  /* eslint-enable */
  window.fbq?.("init", pixelId);
  window.fbq?.("track", "PageView");
}

function initGoogle(cfg: PixelConfig): void {
  if (typeof window === "undefined") return;
  const tagId = cfg.ga4Id || cfg.gadsConversionId;
  if (!tagId) return;
  if (!window.gtag) {
    injectScript(`https://www.googletagmanager.com/gtag/js?id=${tagId}`);
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer!.push(arguments as unknown as unknown[]);
    };
    window.gtag("js", new Date());
  }
  if (cfg.ga4Id) window.gtag!("config", cfg.ga4Id, { send_page_view: true });
  if (cfg.gadsConversionId) window.gtag!("config", cfg.gadsConversionId);
}

/** Idempotent. Loads pixel scripts if IDs are configured. No-op otherwise. */
export function initPixels(): void {
  if (initialized) return;
  initialized = true;
  const cfg = readConfig();
  if (cfg.metaPixelId) initMeta(cfg.metaPixelId);
  if (cfg.ga4Id || cfg.gadsConversionId) initGoogle(cfg);
}

/** Map a funnel event onto Meta + Google conversion APIs. Safe no-op when unconfigured. */
export function pixelTrack(event: FunnelEvent, metadata: Record<string, unknown> = {}): void {
  if (typeof window === "undefined") return;
  const cfg = readConfig();

  // ---- Meta Pixel ----
  if (cfg.metaPixelId && window.fbq) {
    const value = typeof metadata.value === "number" ? metadata.value : undefined;
    const currency = (metadata.currency as string) || "USD";
    switch (event) {
      case "lp_visit":
        window.fbq("track", "ViewContent", { content_name: "lp" });
        break;
      case "lp_cast_started":
        window.fbq("trackCustom", "CastStarted");
        break;
      case "lp_cast_completed":
        window.fbq("track", "Lead", { content_name: "lp_cast" });
        break;
      case "tarot_cast_completed":
        window.fbq("track", "Lead", { content_name: "tarot_cast" });
        break;
      case "founder_modal_shown":
        window.fbq("trackCustom", "FounderModalShown");
        break;
      case "founder_checkout_started":
        window.fbq("track", "InitiateCheckout", { content_name: "founder_offer", value: 5.99, currency: "USD" });
        break;
      case "founder_purchase_success":
        window.fbq("track", "Purchase", { value: value ?? 5.99, currency, content_name: "founder_offer" });
        break;
      case "waitlist_modal_shown":
        window.fbq("trackCustom", "WaitlistModalShown");
        break;
      case "waitlist_submitted":
        window.fbq("track", "Lead", { content_name: "waitlist" });
        break;
      case "auth_started":
        window.fbq("trackCustom", "AuthStarted");
        break;
      case "auth_completed":
        window.fbq("track", "CompleteRegistration");
        break;
      case "referral_link_copied":
        window.fbq("trackCustom", "ReferralLinkCopied");
        break;
      case "referral_signup_attributed":
        window.fbq("trackCustom", "ReferralSignupAttributed");
        break;
      case "first_paid_conversion":
        window.fbq("track", "Purchase", { value: value ?? 0, currency });
        break;
    }
  }

  // ---- Google (GA4 event + Google Ads conversion) ----
  if (window.gtag) {
    // GA4: always emit a named event
    window.gtag("event", event, {
      event_category: "funnel",
      ...metadata,
    });

    // Google Ads: send conversion when a label is configured for this event
    const label = cfg.gadsLabels?.[event];
    if (cfg.gadsConversionId && label) {
      const value = typeof metadata.value === "number" ? metadata.value : undefined;
      const currency = (metadata.currency as string) || "USD";
      window.gtag("event", "conversion", {
        send_to: `${cfg.gadsConversionId}/${label}`,
        ...(value !== undefined ? { value, currency } : {}),
      });
    }
  }
}

export function pixelsConfigured(): boolean {
  const c = readConfig();
  return Boolean(c.metaPixelId || c.ga4Id || c.gadsConversionId);
}
